import type { ReactNode } from 'react';
import { PageLoader } from '@customerio/pluma-components/react';
import Journeys from '@/pages/journeys';
import { LastWorkspaceResolver, LazyPage } from '@/routes/-components';
import { AccountFeatureFlag, useFeatureFlag } from '@/services/auth/feature-flags';
import { useParams } from '@tanstack/react-router';
import { ProtectedRoute } from '@/services/router/components/protected-route';
import { SafeNavigate } from '@/services/router/safe-navigate';
import { useWorkspaceIdFromCurrentUrl } from '@/services/router/use-workspace-id';
import { RumPageRendered } from '@/services/rum/use-page-rendered';
import CreatePage from './create.page';
import EditPage from './edit.page';
import AppsIndexPage from './index.page';
import { useAppsHrefs } from './hooks/use-apps-breadcrumbs';

const SETTINGS_ACCESS_MESSAGE =
  "You do not have permission to view workspace settings. If you'd like access, let an admin know.";

type AppsRouteFrameProps = {
  children: (workspaceId: number) => ReactNode;
};

/**
 * The rollout flag is read above everything else: while it is off these URLs
 * belong to the embedded app, and a gate of ours running first could redirect a
 * visit the embedded app would have served.
 */
function AppsRouteFrame({ children }: AppsRouteFrameProps) {
  const isMigrated = useFeatureFlag(AccountFeatureFlag.PushSettingsMigration);

  if (!isMigrated) {
    return <Journeys />;
  }

  return (
    <LastWorkspaceResolver>
      <ProtectedRoute
        permission="journeys/workspace_settings/view"
        allowReadOnlyMasqAccess={true}
        redirectTo="/settings"
        errorMessage={SETTINGS_ACCESS_MESSAGE}
      >
        <LazyPage>
          <MultiAppGate>{children}</MultiAppGate>
        </LazyPage>
      </ProtectedRoute>
    </LastWorkspaceResolver>
  );
}

type MultiAppGateProps = {
  children: (workspaceId: number) => ReactNode;
};

function MultiAppGate({ children }: MultiAppGateProps) {
  const isMultiApp = useFeatureFlag(AccountFeatureFlag.MultiApp);
  const workspaceId = useWorkspaceIdFromCurrentUrl();
  const hrefs = useAppsHrefs(workspaceId ?? 'last');

  if (!isMultiApp) {
    return <SafeNavigate to={hrefs.settings} options={{ shouldReplaceHistoryEntry: true }} />;
  }

  // The `last` sentinel leaves no id in the URL until the resolver above has
  // swapped it for the real one.
  if (workspaceId == null || workspaceId <= 0) {
    return <PageLoader />;
  }

  return children(workspaceId);
}

export function WorkspaceAppsIndexRoute() {
  return (
    <AppsRouteFrame>
      {(workspaceId) => (
        <RumPageRendered>
          <AppsIndexPage workspaceId={workspaceId} />
        </RumPageRendered>
      )}
    </AppsRouteFrame>
  );
}

export function WorkspaceAppsCreateRoute() {
  return (
    <AppsRouteFrame>
      {(workspaceId) => (
        <RumPageRendered>
          <CreatePage workspaceId={workspaceId} />
        </RumPageRendered>
      )}
    </AppsRouteFrame>
  );
}

export function WorkspaceAppsEditRoute() {
  return <AppsRouteFrame>{(workspaceId) => <AppsEditRouteBody workspaceId={workspaceId} />}</AppsRouteFrame>;
}

type AppsEditRouteBodyProps = {
  workspaceId: number;
};

function toAppId(value: string | undefined): number | null {
  if (value == null || !/^\d+$/.test(value)) {
    return null;
  }
  const appId = Number(value);
  return appId > 0 ? appId : null;
}

function AppsEditRouteBody({ workspaceId }: AppsEditRouteBodyProps) {
  // Not strict: this renders below the route that declares `appId`, and the
  // gates above can render it before that match settles.
  const { appId } = useParams({ strict: false });
  const hrefs = useAppsHrefs(workspaceId);
  const parsed = toAppId(appId);

  if (parsed == null) {
    return <SafeNavigate to={hrefs.index} options={{ shouldReplaceHistoryEntry: true }} />;
  }

  return (
    <RumPageRendered>
      <EditPage workspaceId={workspaceId} appId={parsed} />
    </RumPageRendered>
  );
}
